import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Upload, X } from 'lucide-react';
import type { Database } from '@/lib/database.types';
import { RichTextEditor } from '@/components/RichTextEditor';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

type Producer = Database['public']['Tables']['producers']['Row'];
type ProfileData = Database['public']['Tables']['profiles']['Row'];

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function Profile() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [uploadError, setUploadError] = React.useState('');
  const [producer, setProducer] = React.useState<Producer | null>(null);
  const [profile, setProfile] = React.useState<ProfileData | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    companyName: '',
    description: '',
    websiteUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: ''
  });
  const [profileData, setProfileData] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: ''
  });
  const [visibilitySettings, setVisibilitySettings] = React.useState({
    showName: false,
    showPhone: false,
    showEmail: false
  });
  const [passwordData, setPasswordData] = React.useState({
    newPassword: '',
    confirmPassword: ''
  });

  React.useEffect(() => {
    if (!user) return;

    async function fetchProducer() {
      try {
        // First check if producer profile exists
        const { data, error } = await supabase
          .from('producers')
          .select('*')
          .eq('id', user?.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setProducer(data);
          setFormData({
            companyName: data.company_name,
            description: data.description || '',
            websiteUrl: data.website_url || '',
            facebookUrl: data.facebook_url || '',
            instagramUrl: data.instagram_url || '',
            twitterUrl: data.twitter_url || ''
          });
          setVisibilitySettings({
            showName: data.show_name || false,
            showPhone: data.show_phone || false,
            showEmail: data.show_email || false
          });
          if (data.logo_url) {
            setPreview(data.logo_url);
          }

          // Fetch profile data
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user?.id)
            .maybeSingle();
            
          if (!profileError && profileData) {
            setProfile(profileData);
            setProfileData({
              firstName: profileData.first_name || '',
              lastName: profileData.last_name || '',
              phone: profileData.phone || '',
              address: profileData.address || ''
            });
          } else {
            console.error('Error fetching profile:', profileError);
          }
        }
      } catch (err) {
        console.error('Error fetching producer:', err);
      }
    }

    fetchProducer();
  }, [user]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0];
    setUploadError('');

    if (!selectedFile) return;

    // Validate file
    if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
      setUploadError('Endast JPEG, PNG och SVG-filer är tillåtna');
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setUploadError('Filer måste vara mindre än 5MB');
      return;
    }

    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  }

  function removeFile() {
    setFile(null);
    setPreview(null);
  }

  async function uploadLogo() {
    if (!file || !user) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).slice(2)}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    const { error: uploadError, data } = await supabase.storage
      .from('product_images')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('product_images')
      .getPublicUrl(filePath);

    return publicUrl;
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');
    setPasswordLoading(true);

    const { newPassword, confirmPassword } = passwordData;

    if (newPassword !== confirmPassword) {
      setPasswordError('Lösenorden matchar inte');
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Lösenordet måste vara minst 8 tecken långt');
      setPasswordLoading(false);
      return;
    }

    try {
      // Change password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success('Lösenordet har uppdaterats');
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError('Det gick inte att uppdatera lösenordet. Försök igen senare.');
    } finally {
      setPasswordLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      let logoUrl = producer?.logo_url;
      if (file) {
        logoUrl = await uploadLogo();
      } else if (!preview) {
        logoUrl = null;
      }

      // Update producer information
      const { error: producerError } = await supabase
        .from('producers')
        .upsert({
          id: user.id,
          company_name: formData.companyName,
          description: formData.description,
          logo_url: logoUrl,
          website_url: formData.websiteUrl,
          facebook_url: formData.facebookUrl,
          instagram_url: formData.instagramUrl,
          twitter_url: formData.twitterUrl,
          show_name: visibilitySettings.showName,
          show_phone: visibilitySettings.showPhone,
          show_email: visibilitySettings.showEmail
        });

      if (producerError) throw producerError;

      // Update profile information
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          phone: profileData.phone,
          address: profileData.address
        })
        .eq('id', user.id);

      if (profileError) throw profileError;
      
      toast.success(t('common.profileUpdated'));
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="md:grid md:grid-cols-3 md:gap-6 mb-8">
        <div className="md:col-span-1">
          <h1 className="text-2xl font-semibold text-gray-900">Profil</h1>
          <p className="mt-2 text-sm text-gray-500">
            Uppdatera din producentprofil och sociala medier-länkar.
          </p>
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          <form onSubmit={handleSubmit}>
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <h3 className="text-lg font-medium text-gray-900">Kontaktinformation</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      Förnamn
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Efternamn
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Telefon
                    </label>
                    <input
                      type="text"
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Adress
                    </label>
                    <input
                      type="text"
                      id="address"
                      value={profileData.address}
                      onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Visa kontaktinformation på din offentliga sida
                  </h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Välj vilken kontaktinformation som ska visas på din offentliga producentprofil
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        id="showName"
                        type="checkbox"
                        checked={visibilitySettings.showName}
                        onChange={(e) => setVisibilitySettings({ ...visibilitySettings, showName: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="showName" className="ml-2 text-sm text-gray-700">
                        Visa namn
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="showPhone"
                        type="checkbox"
                        checked={visibilitySettings.showPhone}
                        onChange={(e) => setVisibilitySettings({ ...visibilitySettings, showPhone: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="showPhone" className="ml-2 text-sm text-gray-700">
                        Visa telefonnummer
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="showEmail"
                        type="checkbox"
                        checked={visibilitySettings.showEmail}
                        onChange={(e) => setVisibilitySettings({ ...visibilitySettings, showEmail: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="showEmail" className="ml-2 text-sm text-gray-700">
                        Visa e-post
                      </label>
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <h3 className="text-lg font-medium text-gray-900">Företagsinformation</h3>
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                    Företagsnamn
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    required
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Beskrivning
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Kort beskrivning av ditt företag och produkter."
                    className="mt-1"
                    height={200}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Kort beskrivning av ditt företag och produkter.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Företagslogotyp
                  </label>
                  {uploadError && (
                    <p className="mt-1 text-sm text-red-600">{uploadError}</p>
                  )}
                  <div className="mt-2">
                    <div className="flex items-center space-x-4">
                      {preview ? (
                        <div className="relative">
                          <img
                            src={preview}
                            alt="Logo preview"
                            className="h-24 w-24 object-contain bg-gray-50 rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={removeFile}
                            className="absolute -top-2 -right-2 bg-red-100 rounded-full p-1 text-red-600 hover:bg-red-200"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <label className="h-24 w-24 flex flex-col items-center justify-center border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-green-500 bg-gray-50">
                          <Upload className="h-8 w-8 text-gray-400" />
                          <span className="mt-2 text-sm text-gray-500">Lägg till bild</span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.svg"
                            onChange={handleFileChange}
                          />
                        </label>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      Godkända format: JPEG, PNG, SVG. Max filstorlek: 5MB
                    </p>
                  </div>
                </div>

                <div>
                  <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700">
                    Webbplats
                  </label>
                  <input
                    type="text"
                    id="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    placeholder="https://example.com"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Frivilligt</p>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div>
                    <label htmlFor="facebookUrl" className="block text-sm font-medium text-gray-700">
                      Facebook
                    </label>
                    <input
                      type="text"
                      id="facebookUrl"
                      value={formData.facebookUrl}
                      onChange={(e) => setFormData({ ...formData, facebookUrl: e.target.value })}
                      placeholder="https://facebook.com/your-page"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Frivilligt</p>
                  </div>

                  <div>
                    <label htmlFor="instagramUrl" className="block text-sm font-medium text-gray-700">
                      Instagram
                    </label>
                    <input
                      type="text"
                      id="instagramUrl"
                      value={formData.instagramUrl}
                      onChange={(e) => setFormData({ ...formData, instagramUrl: e.target.value })}
                      placeholder="https://instagram.com/your-handle"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Frivilligt</p>
                  </div>

                  <div>
                    <label htmlFor="twitterUrl" className="block text-sm font-medium text-gray-700">
                      Twitter
                    </label>
                    <input
                      type="text"
                      id="twitterUrl"
                      value={formData.twitterUrl}
                      onChange={(e) => setFormData({ ...formData, twitterUrl: e.target.value })}
                      placeholder="https://twitter.com/your-handle"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Frivilligt</p>
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sparar...' : 'Spara'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Password Change Section */}
      <div className="md:grid md:grid-cols-3 md:gap-6 mt-8">
        <div className="md:col-span-1">
          <h1 className="text-2xl font-semibold text-gray-900">Ändra lösenord</h1>
          <p className="mt-2 text-sm text-gray-500">
            Uppdatera ditt lösenord för att hålla ditt konto säkert.
          </p>
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          <form onSubmit={handlePasswordSubmit}>
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {passwordError && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{passwordError}</div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                      Nytt lösenord
                    </label>
                    <input
                      type="password"
                      id="new-password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                      Bekräfta lösenord
                    </label>
                    <input
                      type="password"
                      id="confirm-password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? 'Uppdaterar...' : 'Uppdatera lösenord'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}